import React from "react";

import { useMutation } from "@apollo/client";
import { MenuItem, Select } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { TrackingStatus } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";
import { humanize } from "@/utils/string";

interface Props {
  clientId: string;
  value?: TrackingStatus;
}

const updateTrackingStatusMutation = graphql(`
  mutation UpdateTrackingStatus($input: UpdateTrackingStatusInput!) {
    updateTrackingStatus(input: $input) {
      client {
        id
        trackingStatus
      }
    }
  }
`);

const trackingStatusColors = {
  [TrackingStatus.Active]: "#1FD4AF",
  [TrackingStatus.Cancelled]: "#C8CED8",
  [TrackingStatus.NoAction]: "#F27373",
  [TrackingStatus.Onboarding]: "#6EE2EA",
  [TrackingStatus.Pending]: "#FFB41E",
  [TrackingStatus.TestOnly]: "#112340",
};

const useStyles = makeStyles()({
  menuItem: {
    whiteSpace: "nowrap",
  },
  dot: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 9999,
    marginRight: 20,
  },
});

const TrackingStatusSelect: React.FC<Props> = ({ clientId, value }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [_updateTrackingStatus] = useMutation(updateTrackingStatusMutation);

  const updateTrackingStatus = async (status: TrackingStatus) => {
    try {
      _updateTrackingStatus({
        variables: { input: { clientId, trackingStatus: status } },
        optimisticResponse: {
          __typename: "Mutation",
          updateTrackingStatus: {
            __typename: "UpdateTrackingStatusPayload",
            client: {
              __typename: "Client",
              id: clientId,
              trackingStatus: status,
            },
          },
        },
      });
    } catch (e) {
      snackbar.open(displayError(e, "Unable to update status."));
    }
  };

  return (
    <Select
      value={value}
      onChange={(e) => {
        updateTrackingStatus(e.target.value as TrackingStatus);
      }}
      fullWidth
    >
      {Object.values(TrackingStatus).map((status) => (
        <MenuItem key={status} className={classes.menuItem} value={status}>
          <div
            className={classes.dot}
            style={{ backgroundColor: trackingStatusColors[status] }}
          />
          {humanize(status)}
        </MenuItem>
      ))}
    </Select>
  );
};

export default TrackingStatusSelect;
